import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function TermVsWholeLifeIndianapolisHomeowners() {
  useEffect(() => {
    const SITE_URL = 'https://hoosierlifeinsurance.com';
    document.title = "Term vs Whole Life: Indianapolis Homeowners Guide";

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Term vs. whole life insurance for Indianapolis homeowners: which protects your mortgage better? Real cost comparisons and examples from Indiana families.');

    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', 'term vs whole life insurance Indianapolis homeowners, term life vs whole life Indiana, Indianapolis mortgage protection insurance');

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/resources/term-vs-whole-life-indianapolis-homeowners`;
  }, []);

  return (
    <>
      <title>Term vs Whole Life: Indianapolis Homeowners Guide | Hoosier Life Insurance</title>
      <article className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white py-20">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-4xl mx-auto px-6">
            <div className="mb-6">
              <Link to="/resources" className="inline-flex items-center text-white/70 hover:text-white transition-colors text-sm">
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Resources
              </Link>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Term vs. Whole Life Insurance for Indianapolis Homeowners</h1>
            <p className="text-xl text-white/80 mb-8">Which policy type protects your mortgage and family best? A complete comparison for Indiana homeowners.</p>
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              <span><i className="ri-calendar-line mr-2"></i>Updated January 2026</span>
              <span><i className="ri-time-line mr-2"></i>10 min read</span>
              <span><i className="ri-map-pin-line mr-2"></i>Indianapolis &amp; Central Indiana</span>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              You just bought a home in Indianapolis—congratulations! Now comes the question every Indiana homeowner faces: <strong>Should I get term life insurance or whole life insurance to protect my mortgage?</strong>
            </p>
            <p className="text-gray-700 leading-relaxed">
              The answer depends on your goals, budget, and how long you need coverage. This guide breaks down both options with real Indianapolis examples, cost comparisons, and expert recommendations to help you make the right choice for your family.
            </p>
          </div>

          {/* Quick Answer */}
          <div className="bg-[#FDF6F0] border-l-4 border-[#D97642] p-6 mb-12 rounded-r-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
              <i className="ri-lightbulb-line text-[#D97642] mr-3 text-2xl"></i>
              Quick Answer for Most Indianapolis Homeowners
            </h3>
            <p className="text-gray-800 leading-relaxed mb-4">
              <strong>90% of Indianapolis homeowners should choose term life insurance for mortgage protection.</strong> It's 8-10x cheaper than whole life, provides the exact coverage you need for your mortgage term (15-30 years), and frees up money for retirement savings and home improvements.
            </p>
            <p className="text-gray-700 text-sm">
              <strong>Exception:</strong> High-net-worth homeowners in Carmel, Zionsville, or Geist who want permanent coverage, estate planning benefits, and tax-free wealth accumulation should consider whole life.
            </p>
          </div>

          {/* Side-by-Side Comparison */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Term vs. Whole Life: Side-by-Side Comparison</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-6 py-4 text-left font-bold">Feature</th>
                    <th className="px-6 py-4 text-left font-bold">Term Life Insurance</th>
                    <th className="px-6 py-4 text-left font-bold">Whole Life Insurance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Coverage Duration</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">10, 15, 20, or 30 years</td>
                    <td className="px-6 py-4 text-gray-700">Lifetime (until age 100+)</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-bold text-gray-900">Monthly Cost (Age 35, $500K)</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$25-$35/month</td>
                    <td className="px-6 py-4 text-red-600 font-bold">$350-$450/month</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-bold text-gray-900">Cash Value</td>
                    <td className="px-6 py-4 text-gray-700">None</td>
                    <td className="px-6 py-4 text-gray-700">Builds tax-deferred cash value</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-bold text-gray-900">Best For</td>
                    <td className="px-6 py-4 text-gray-700">Mortgage protection, income replacement</td>
                    <td className="px-6 py-4 text-gray-700">Estate planning, wealth transfer, permanent needs</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-bold text-gray-900">Flexibility</td>
                    <td className="px-6 py-4 text-gray-700">Can cancel anytime, no cash value lost</td>
                    <td className="px-6 py-4 text-gray-700">Surrender charges if canceled early</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-bold text-gray-900">Ideal Mortgage Match</td>
                    <td className="px-6 py-4 text-gray-700">30-year term = 30-year mortgage</td>
                    <td className="px-6 py-4 text-gray-700">Permanent coverage beyond mortgage payoff</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-bold text-gray-900">Tax Benefits</td>
                    <td className="px-6 py-4 text-gray-700">Death benefit tax-free</td>
                    <td className="px-6 py-4 text-gray-700">Death benefit + cash value growth tax-free</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-bold text-gray-900">Typical Indianapolis User</td>
                    <td className="px-6 py-4 text-gray-700">Young families, first-time homeowners</td>
                    <td className="px-6 py-4 text-gray-700">High earners, business owners, estate planners</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Real Indianapolis Example */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Real Indianapolis Example: The Johnson Family</h2>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-home-heart-line text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Meet the Johnsons</h3>
                  <ul className="space-y-1 text-gray-700">
                    <li><strong>Location:</strong> Fishers, IN (Hamilton County)</li>
                    <li><strong>Ages:</strong> Mike (35), Sarah (33)</li>
                    <li><strong>Children:</strong> 2 kids (ages 5 and 3)</li>
                    <li><strong>Home:</strong> $350,000 (30-year mortgage, $280,000 remaining)</li>
                    <li><strong>Household Income:</strong> $110,000/year</li>
                  </ul>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Term Life Option */}
                <div className="bg-white rounded-xl p-6 border-2 border-teal-500">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center">
                      <i className="ri-calendar-check-line text-xl"></i>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900">Option 1: Term Life</h4>
                  </div>
                  <ul className="space-y-3 text-gray-700 mb-6">
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                      <span><strong>Coverage:</strong> $500,000 (30-year term)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                      <span><strong>Monthly Cost:</strong> $65 (both spouses)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                      <span><strong>Total Paid (30 years):</strong> $23,400</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                      <span><strong>Coverage Ends:</strong> Age 65 (mortgage paid off)</span>
                    </li>
                  </ul>
                  <div className="bg-teal-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700"><strong>Result:</strong> Mortgage fully protected. Kids through college. $385/month saved vs. whole life invested = $462,000 at retirement.</p>
                  </div>
                </div>

                {/* Whole Life Option */}
                <div className="bg-white rounded-xl p-6 border-2 border-gray-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gray-600 text-white rounded-lg flex items-center justify-center">
                      <i className="ri-infinity-line text-xl"></i>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900">Option 2: Whole Life</h4>
                  </div>
                  <ul className="space-y-3 text-gray-700 mb-6">
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-gray-600 mr-3 mt-1"></i>
                      <span><strong>Coverage:</strong> $500,000 (lifetime)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-gray-600 mr-3 mt-1"></i>
                      <span><strong>Monthly Cost:</strong> $800 (both spouses)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-gray-600 mr-3 mt-1"></i>
                      <span><strong>Total Paid (30 years):</strong> $288,000</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-gray-600 mr-3 mt-1"></i>
                      <span><strong>Cash Value (30 years):</strong> ~$180,000</span>
                    </li>
                  </ul>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700"><strong>Result:</strong> Permanent coverage + cash value. But $735/month less for retirement, college, home improvements.</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-teal-600 text-white rounded-xl p-6">
                <h4 className="text-xl font-bold mb-3">The Johnsons Chose Term Life. Here's Why:</h4>
                <ul className="space-y-2 text-teal-50">
                  <li className="flex items-start">
                    <i className="ri-check-line mr-3 mt-1 text-xl"></i>
                    <span>Saved $735/month = $8,820/year for 401(k), 529 college savings, and home renovations</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line mr-3 mt-1 text-xl"></i>
                    <span>Mortgage fully protected for 30 years (exactly matches their loan term)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line mr-3 mt-1 text-xl"></i>
                    <span>By age 65, they'll have $462,000 more in retirement accounts than if they'd chosen whole life</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line mr-3 mt-1 text-xl"></i>
                    <span>No permanent coverage needed—kids will be independent, mortgage paid off</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* When to Choose Term Life */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">When Indianapolis Homeowners Should Choose Term Life</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border-2 border-[#D97642] rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#1A3A52] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You're a First-Time Homeowner</h3>
                </div>
                <p className="text-gray-700 text-sm">
                  Just bought in Broad Ripple, Fountain Square, or Irvington? Term life matches your 30-year mortgage perfectly and costs $30-$50/month for $500K coverage.
                </p>
              </div>

              <div className="bg-white border-2 border-[#D97642] rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#1A3A52] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-parent-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You Have Young Children</h3>
                </div>
                <p className="text-gray-700 text-sm">
                  Need coverage until kids finish college? A 20-year term policy protects your mortgage and family through their most expensive years.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Budget Is a Priority</h3>
                </div>
                <p className="text-gray-700 text-sm">
                  Term life costs 8-10x less than whole life. You can afford $1 million in coverage for what whole life charges for $100K.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-line-chart-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You're Maxing Out Retirement Accounts</h3>
                </div>
                <p className="text-gray-700 text-sm">
                  Already contributing to 401(k) and Roth IRA? Term life frees up money for investments that grow faster than whole life cash value.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-event-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Your Need Is Temporary</h3>
                </div>
                <p className="text-gray-700 text-sm">
                  Once your mortgage is paid off and kids are independent, you won't need life insurance. Term life covers exactly that window.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-exchange-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You Want Flexibility</h3>
                </div>
                <p className="text-gray-700 text-sm">
                  Term policies have no surrender charges. Cancel anytime if your needs change—no penalties, no cash value lost.
                </p>
              </div>
            </div>
          </div>

          {/* When to Choose Whole Life */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">When Indianapolis Homeowners Should Choose Whole Life</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You're a High-Net-Worth Homeowner</h3>
                </div>
                <p className="text-gray-700 text-sm">
                  Own a $750K+ home in Carmel, Zionsville, or Geist? Whole life provides estate planning benefits and tax-free wealth transfer to heirs.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-briefcase-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You Own a Business</h3>
                </div>
                <p className="text-gray-700 text-sm">
                  Business owners use whole life for buy-sell agreements, key person insurance, and tax-advantaged retirement funding.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-hand-heart-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You Have a Special Needs Dependent</h3>
                </div>
                <p className="text-gray-700 text-sm">
                  Permanent coverage ensures lifelong financial support for a child or family member who will always depend on you.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-gift-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You Want to Leave a Legacy</h3>
                </div>
                <p className="text-gray-700 text-sm">
                  Whole life guarantees a tax-free death benefit to your children, grandchildren, or favorite charity—no matter when you pass.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-bank-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You've Maxed Out All Other Tax-Advantaged Accounts</h3>
                </div>
                <p className="text-gray-700 text-sm">
                  Already maxing 401(k), IRA, HSA, and 529? Whole life offers additional tax-deferred growth and tax-free loans.
                </p>
              </div>

              <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-heart-pulse-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">You Have Health Issues</h3>
                </div>
                <p className="text-gray-700 text-sm">
                  If you're uninsurable for term life later, whole life locks in coverage now—guaranteed for life, regardless of future health changes.
                </p>
              </div>
            </div>
          </div>

          {/* Cost Comparison */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Real Indianapolis Costs: Term vs. Whole Life</h2>
            <p className="text-gray-700 mb-8">
              Here's what Indianapolis homeowners actually pay for $500,000 in coverage (rates for healthy non-smokers):
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-6 py-4 text-left font-bold">Age</th>
                    <th className="px-6 py-4 text-left font-bold">Term Life (30-Year)</th>
                    <th className="px-6 py-4 text-left font-bold">Whole Life</th>
                    <th className="px-6 py-4 text-left font-bold">Monthly Savings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 30</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$28/month</td>
                    <td className="px-6 py-4 text-gray-700">$380/month</td>
                    <td className="px-6 py-4 text-green-600 font-bold">Save $352/month</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 35</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$32/month</td>
                    <td className="px-6 py-4 text-gray-700">$420/month</td>
                    <td className="px-6 py-4 text-green-600 font-bold">Save $388/month</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 40</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$45/month</td>
                    <td className="px-6 py-4 text-gray-700">$480/month</td>
                    <td className="px-6 py-4 text-green-600 font-bold">Save $435/month</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 45</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$68/month</td>
                    <td className="px-6 py-4 text-gray-700">$550/month</td>
                    <td className="px-6 py-4 text-green-600 font-bold">Save $482/month</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 50</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$115/month</td>
                    <td className="px-6 py-4 text-gray-700">$640/month</td>
                    <td className="px-6 py-4 text-green-600 font-bold">Save $525/month</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-6 bg-teal-50 rounded-xl p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-3">What Could You Do With the Savings?</h4>
              <p className="text-gray-700 mb-4">
                If a 35-year-old chooses term life and invests the $388/month savings in a Roth IRA earning 8% annually:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                  <span><strong>After 10 years:</strong> $71,000 (vs. $18,000 cash value in whole life)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                  <span><strong>After 20 years:</strong> $229,000 (vs. $85,000 cash value in whole life)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                  <span><strong>After 30 years:</strong> $568,000 (vs. $180,000 cash value in whole life)</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Hybrid Option */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">The Hybrid Strategy: Best of Both Worlds</h2>
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-stack-line text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Combine Term + Whole Life for Maximum Protection</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Many Indianapolis homeowners use a <strong>layered approach</strong>: term life for temporary needs (mortgage, kids) + a smaller whole life policy for permanent coverage and cash value.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Example: The Hybrid Homeowner</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="font-bold">1</span>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">$750,000 Term Life (30-Year)</p>
                      <p className="text-sm text-gray-700">Covers mortgage + income replacement. Cost: $55/month</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="font-bold">2</span>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">$100,000 Whole Life (Permanent)</p>
                      <p className="text-sm text-gray-700">Covers final expenses + small legacy. Cost: $85/month</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t-2 border-gray-200">
                  <p className="text-gray-900"><strong>Total Monthly Cost:</strong> $140/month</p>
                  <p className="text-sm text-gray-600 mt-2">This gives you $850,000 total coverage—$750K temporary for mortgage/kids, $100K permanent for final expenses and legacy.</p>
                </div>
              </div>

              <div className="bg-teal-600 text-white rounded-xl p-6">
                <h4 className="text-xl font-bold mb-3">Why This Works for Indianapolis Homeowners</h4>
                <ul className="space-y-2 text-teal-50">
                  <li className="flex items-start">
                    <i className="ri-check-line mr-3 mt-1 text-xl"></i>
                    <span>Affordable: $140/month vs. $800/month for $850K whole life</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line mr-3 mt-1 text-xl"></i>
                    <span>Flexible: Drop term life after mortgage is paid, keep whole life forever</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line mr-3 mt-1 text-xl"></i>
                    <span>Balanced: High coverage now + permanent protection later</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Common Mistakes */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">5 Mistakes Indianapolis Homeowners Make</h2>
            <div className="space-y-4">
              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #1: Buying Mortgage Life Insurance from Your Lender
                </h3>
                <p className="text-gray-700 text-sm">
                  Lender-sold mortgage insurance is 2-3x more expensive than term life, and the death benefit goes to the bank, not your family. Always buy your own term life policy instead.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #2: Choosing Whole Life Because "It's an Investment"
                </h3>
                <p className="text-gray-700 text-sm">
                  Whole life cash value grows at 2-4% annually. A Roth IRA or 401(k) grows at 8-10%. Buy term life and invest the difference for better returns.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #3: Underinsuring to Afford Whole Life
                </h3>
                <p className="text-gray-700 text-sm">
                  Don't buy $100K whole life when you need $500K coverage. It's better to have $500K term life for 30 years than $100K whole life forever.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #4: Not Insuring Both Spouses
                </h3>
                <p className="text-gray-700 text-sm">
                  Even if one spouse stays home, they provide $40K-$60K/year in childcare, housekeeping, and transportation. Both spouses need coverage.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #5: Waiting Until You "Need" It
                </h3>
                <p className="text-gray-700 text-sm">
                  Life insurance costs increase 8-10% per year of age. A 30-year-old pays $28/month; a 40-year-old pays $45/month for the same coverage. Buy now while you're young and healthy.
                </p>
              </div>
            </div>
          </div>

          {/* Decision Framework */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Quick Decision Framework</h2>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold">
                    ?
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Ask Yourself These 3 Questions:</h3>
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
                        <p className="font-bold text-gray-900 mb-2">1. How long do I need coverage?</p>
                        <p className="text-sm text-gray-700">
                          <strong>Until mortgage is paid / kids are independent:</strong> <span className="text-teal-600 font-bold">Choose Term Life</span><br />
                          <strong>Forever (estate planning, legacy):</strong> <span className="text-gray-600 font-bold">Choose Whole Life</span>
                        </p>
                      </div>

                      <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
                        <p className="font-bold text-gray-900 mb-2">2. What's my budget?</p>
                        <p className="text-sm text-gray-700">
                          <strong>Under $100/month:</strong> <span className="text-teal-600 font-bold">Choose Term Life</span><br />
                          <strong>$300+/month and maxing retirement accounts:</strong> <span className="text-gray-600 font-bold">Consider Whole Life</span>
                        </p>
                      </div>

                      <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
                        <p className="font-bold text-gray-900 mb-2">3. What's my net worth?</p>
                        <p className="text-sm text-gray-700">
                          <strong>Under $1 million:</strong> <span className="text-teal-600 font-bold">Choose Term Life</span><br />
                          <strong>$1 million+ and need estate planning:</strong> <span className="text-gray-600 font-bold">Consider Whole Life or Hybrid</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 mb-12">
            <h2 className="text-3xl font-bold mb-6">Get a Free Quote for Your Indianapolis Home</h2>
            <p className="text-xl text-white/80 mb-8">
              Let's find the right coverage type and amount for your family. Compare term and whole life quotes in 60 seconds.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <Link 
                to="/instant-quote" 
                className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Get Instant Quote
              </Link>
              <Link 
                to="/book-appointment" 
                className="bg-[#0F2A3F] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0A1F2E] transition-colors border-2 border-white/30 whitespace-nowrap"
              >
                Talk to an Agent
              </Link>
            </div>
          </div>

          {/* Related Articles */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>

            {/* Product Pages — two-way authority signal */}
            <div className="mb-4">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Product Pages</p>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <Link to="/term-life" className="group bg-white border-2 border-[#D97642] rounded-xl p-5 hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-calendar-check-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Term Life Insurance</h3>
                  <p className="text-xs text-gray-500">Compare term rates from 20+ Indiana carriers</p>
                </Link>
                <Link to="/whole-life" className="group bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-infinity-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Whole Life Insurance</h3>
                  <p className="text-xs text-gray-500">Explore permanent coverage with cash value growth</p>
                </Link>
                <Link to="/mortgage-protection" className="group bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-home-heart-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Mortgage Protection Insurance</h3>
                  <p className="text-xs text-gray-500">Protect your home from $30/month</p>
                </Link>
              </div>
            </div>

            {/* Articles */}
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Articles &amp; Guides</p>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/resources/how-much-life-insurance-indiana-families-need" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-calculator-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">How Much Coverage Do You Need?</h3>
                <p className="text-sm text-gray-600">Calculate the right amount using the DIME method</p>
              </Link>

              <Link to="/blog/life-insurance-rates-indianapolis" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-price-tag-3-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">Life Insurance Rates in Indianapolis</h3>
                <p className="text-sm text-gray-600">Current rate tables — see what term and whole life actually cost</p>
              </Link>

              <Link to="/resources/life-insurance-mistakes-indiana-families" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-error-warning-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">7 Mistakes Indiana Families Make</h3>
                <p className="text-sm text-gray-600">Avoid costly coverage errors — including the whole life vs. term trap</p>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Still Not Sure Which Is Right for You?</h2>
            <p className="text-xl text-gray-700 mb-8">
              Our Indianapolis-based agents help homeowners compare term and whole life every day. Let's review your situation and find the perfect fit.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:3176482983" className="bg-[#1A3A52] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#0F2A3F] transition-colors whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-phone-line mr-2"></i>
                Call (317) 648-2983
              </a>
              <Link to="/contact" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors border-2 border-[#1A3A52] whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-message-3-line mr-2"></i>
                Send a Message
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}